import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SERVICES_CATALOG } from '../data/servicesCatalog';
import { 
  Brain, Code, Settings, Globe, Palette, Rocket, BarChart3, HeartHandshake,
  ChevronDown, ChevronUp, Smartphone, Bot, Workflow, TrendingUp, Users,
  Check, ArrowRight
} from 'lucide-react';

const iconMap = {
  Brain, Code, Settings, Globe, Palette, Rocket, BarChart3, HeartHandshake,
  Smartphone, Bot, Workflow, TrendingUp, Users
};

const ServicesExpanded = () => {
  const { language } = useLanguage();
  const catalog = SERVICES_CATALOG[language];
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  const title = language === 'es' ? 'Servicios Completos' : 'Complete Services';
  const subtitle = language === 'es' 
    ? 'Todo lo que necesitas para transformar tu negocio digitalmente'
    : 'Everything you need to digitally transform your business';
  const coreTitle = language === 'es' ? 'Servicios Core' : 'Core Services';
  const viewDetails = language === 'es' ? 'Ver detalles' : 'View details';
  const hideDetails = language === 'es' ? 'Ocultar' : 'Hide';
  const startingAt = language === 'es' ? 'Desde' : 'Starting at';
  const perMonth = language === 'es' ? '/mes' : '/mo';
  const perHour = language === 'es' ? '/hora' : '/hr';

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <section id="services-full" className="services-expanded" data-testid="services-expanded">
      <div className="container">
        {/* Core Services Grid */}
        <div className="core-services-section">
          <h3 className="core-services-title">{coreTitle}</h3>
          <div className="core-services-grid">
            {catalog.coreServices.map((service, index) => {
              const Icon = iconMap[service.icon] || Globe;
              return (
                <div key={index} className="core-service-item" data-testid={`core-service-${index}`}>
                  <Icon size={20} />
                  <span>{service.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section Header */}
        <div className="services-expanded-header">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>

        {/* Categories Accordion */}
        <div className="services-categories">
          {catalog.categories.map((category) => {
            const CategoryIcon = iconMap[category.icon] || Globe;
            const isExpanded = expandedCategory === category.id;
            
            return (
              <div 
                key={category.id} 
                className={`service-category ${isExpanded ? 'expanded' : ''}`}
                data-testid={`service-category-${category.id}`}
              >
                <button 
                  className="category-header"
                  onClick={() => toggleCategory(category.id)}
                  style={{ '--category-color': category.color }}
                >
                  <div className="category-header-left">
                    <div className="category-icon" style={{ backgroundColor: `${category.color}20` }}>
                      <CategoryIcon size={24} style={{ color: category.color }} />
                    </div>
                    <div className="category-info">
                      <h3>{category.name}</h3>
                      <span className="category-count">
                        {category.services.length} {language === 'es' ? 'servicios' : 'services'}
                      </span>
                    </div>
                  </div>
                  <div className="category-header-right">
                    <span className="category-toggle-text">
                      {isExpanded ? hideDetails : viewDetails}
                    </span>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="category-services">
                    {category.services.map((service) => (
                      <div 
                        key={service.id} 
                        className="service-item"
                        data-testid={`service-item-${service.id}`}
                      >
                        <div className="service-item-content">
                          <div className="service-item-check">
                            <Check size={16} style={{ color: category.color }} />
                          </div>
                          <div className="service-item-info">
                            <h4>{service.name}</h4>
                            <p>{service.description}</p>
                          </div>
                        </div>
                        <div className="service-item-price">
                          <span className="price-label">{startingAt}</span>
                          <span className="price-value">
                            ${service.basePrice.toLocaleString()}
                            {service.perMonth && <span className="price-suffix">{perMonth}</span>}
                            {service.perHour && <span className="price-suffix">{perHour}</span>}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="services-cta">
          <a href="#consultation" className="btn btn-primary btn-lg" data-testid="services-cta-button">
            {language === 'es' ? 'Consulta Gratuita' : 'Free Consultation'}
            <ArrowRight size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesExpanded;
